"use client"

import { useState, useCallback, useMemo } from "react"
import { Search, X, PenSquare, Trash2, Plus, ChevronLeft, Lock, Settings2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useTranslation } from "@/hooks/use-translation"
import { useLocalizedField } from "@/utils/language-utils"
import { useWorkflow } from "@/context/workflow-context-simple"
import { useWorkflowApi } from "@/hooks/use-workflow-api"
import { useToast } from "@/hooks/use-toast"
import { useViewManager } from "../view-manager-context"
import { FieldEditPanel, fieldEditDirtyRef } from "../panels/field-edit-panel"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

export function CustomFieldsStep() {
  const { t } = useTranslation()
  const { state, dispatch } = useViewManager()
  const { configuration } = useWorkflow()
  const { deleteFieldConfiguration } = useWorkflowApi()
  const { toast } = useToast()
  const getLocalizedField = useLocalizedField()

  const [searchTerm, setSearchTerm] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<any>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Permissions
  const authorizedActions = (configuration?.authorizedActions || []).map((a: any) => typeof a === 'string' ? a : a.actionCode)
  const canEditColumns = authorizedActions.includes("EditColumns")
  const canDeleteCustom = authorizedActions.includes("DeleteCustomColumns")

  // Get custom fields — use view designer's config if available, otherwise fall back to global config
  const fieldsSource = state.originalConfig?.fieldsInUse || configuration?.fieldsInUse || []
  const customFields = useMemo(() => {
    const fields = fieldsSource.filter((f: any) => f.isCustomField === true)
    return fields.sort((a: any, b: any) => {
      const nameA = (getLocalizedField(a, 'columnName') || a.columnCode || '').toLowerCase()
      const nameB = (getLocalizedField(b, 'columnName') || b.columnCode || '').toLowerCase()
      return nameA.localeCompare(nameB)
    })
  }, [fieldsSource, getLocalizedField])

  const filteredFields = useMemo(() => {
    if (!searchTerm.trim()) return customFields
    const q = searchTerm.toLowerCase()
    return customFields.filter((f: any) => {
      const name = getLocalizedField(f, 'columnName') || f.columnCode || ''
      return name.toLowerCase().includes(q)
    })
  }, [customFields, searchTerm, getLocalizedField])

  // Select a field for editing in the side panel — warn if current has unsaved changes
  const handleEditField = useCallback((field: any) => {
    if (fieldEditDirtyRef.current && field.fieldConfigurationId !== state.selectedFieldId) {
      const confirmed = window.confirm('You have unsaved field changes. Discard them?')
      if (!confirmed) return
    }
    dispatch({ type: 'SELECT_FIELD', payload: field.fieldConfigurationId })
  }, [dispatch, state.selectedFieldId])

  // Delete a custom field
  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return
    setIsDeleting(true)
    try {
      await deleteFieldConfiguration(deleteTarget.fieldConfigurationId)

      const updatedFieldsInUse = (state.originalConfig?.fieldsInUse || [])
        .filter((f: any) => f.fieldConfigurationId !== deleteTarget.fieldConfigurationId)
      const updatedStageConfigs = (state.originalConfig?.stageConfigurations || []).map((stage: any) => ({
        ...stage,
        fieldConfigurations: (stage.fieldConfigurations || [])
          .filter((f: any) => f.fieldConfigurationId !== deleteTarget.fieldConfigurationId),
      }))

      dispatch({
        type: 'UPDATE_FIELD_METADATA',
        payload: { fieldsInUse: updatedFieldsInUse, stageConfigurations: updatedStageConfigs },
      })

      // Remove from draft columns
      for (const stage of (state.originalConfig?.stageConfigurations || [])) {
        const stageId = stage.stageId || stage.stageCode
        const cols = state.draftStageColumns[stageId] || []
        if (cols.includes(deleteTarget.fieldConfigurationId)) {
          dispatch({
            type: 'REORDER_COLUMNS',
            payload: { stageId, fieldIds: cols.filter((id: string) => id !== deleteTarget.fieldConfigurationId) },
          })
        }
      }

      // Clear selection if the deleted field was selected
      if (state.selectedFieldId === deleteTarget.fieldConfigurationId) {
        dispatch({ type: 'SELECT_FIELD', payload: null })
      }

      toast({ variant: "success", title: t('success') || "Success", description: t('fieldDeleted') || "Field deleted" })
    } catch (err) {
      toast({ variant: "destructive", title: t('error') || "Error", description: err instanceof Error ? err.message : "Failed to delete field" })
    } finally {
      setIsDeleting(false)
      setDeleteTarget(null)
    }
  }, [deleteTarget, deleteFieldConfiguration, state, dispatch, toast, t])

  // Go back
  const handleBack = useCallback(() => {
    if (fieldEditDirtyRef.current) {
      const confirmed = window.confirm('You have unsaved field changes. Discard them?')
      if (!confirmed) return
      fieldEditDirtyRef.current = false
    }
    dispatch({ type: 'SELECT_FIELD', payload: null })
    dispatch({ type: 'SET_STEP', payload: state.selectedView ? 'fields' : 'views' })
  }, [dispatch, state.selectedView])

  return (
    <div className="flex h-full overflow-hidden">
      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header — back + title on same row */}
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={handleBack} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium">
              <ChevronLeft size={16} />
              Back
            </button>
            <div className="h-5 w-px bg-gray-300" />
            <Settings2 size={18} className="text-[#0F214B]" />
            <h2 className="text-base font-semibold text-[#0F214B]">
              {t('manageCustomFields' as any) || 'Manage Custom Fields'}
            </h2>
            <span className="text-xs text-gray-400 ml-1">({customFields.length})</span>
            {canEditColumns && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { dispatch({ type: 'SELECT_FIELD', payload: null }); dispatch({ type: 'SET_ADDING_CUSTOM_FIELD', payload: true }) }}
                className="ml-auto flex items-center gap-1.5 text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
              >
                <Plus size={14} />
                {t('addNewField') || 'Add new field'}
              </Button>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="px-6 py-3 flex-shrink-0">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              placeholder={t('search') || "Search custom fields..."}
              className="pl-9 pr-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Field list */}
        <div className="flex-1 overflow-y-auto px-6 pb-4">
          {filteredFields.length === 0 ? (
            <div className="text-sm text-gray-400 text-center py-12">
              {searchTerm ? 'No matching custom fields' : (t('noCustomFieldsAvailable') || 'No custom fields available')}
            </div>
          ) : (
            <div className="max-w-2xl space-y-1">
              {filteredFields.map((field: any) => {
                const name = getLocalizedField(field, 'columnName') || field.columnCode
                const typeLabel = field.customFieldInputTypeCode || field.columnTypeCode || ''
                const isSelected = state.selectedFieldId === field.fieldConfigurationId
                return (
                  <div
                    key={field.fieldConfigurationId}
                    className={`flex items-center justify-between px-4 py-3 rounded-lg border transition-colors cursor-pointer ${
                      isSelected
                        ? 'bg-blue-50 border-blue-200 shadow-sm'
                        : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                    }`}
                    onClick={() => handleEditField(field)}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      {field.isPrivate && <Lock size={14} className="text-gray-400 flex-shrink-0" />}
                      <span className={`text-sm font-medium truncate ${isSelected ? 'text-blue-700' : 'text-gray-800'}`}>{name}</span>
                      <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded font-medium flex-shrink-0">
                        {typeLabel}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                      {canEditColumns && (
                        <button
                          onClick={() => handleEditField(field)}
                          className="p-1.5 rounded-md border border-transparent hover:border-blue-200 hover:bg-blue-50 text-gray-500 hover:text-blue-600 transition-all"
                          title="Edit field"
                        >
                          <PenSquare size={15} />
                        </button>
                      )}
                      {canDeleteCustom && (
                        <button
                          onClick={() => setDeleteTarget(field)}
                          className="p-1.5 rounded-md border border-transparent hover:border-red-200 hover:bg-red-50 text-gray-500 hover:text-red-500 transition-all"
                          title="Delete field"
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Bottom add button */}
          {canEditColumns && (
            <div className="p-4 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={() => { dispatch({ type: 'SELECT_FIELD', payload: null }); dispatch({ type: 'SET_ADDING_CUSTOM_FIELD', payload: true }) }}
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                <Plus size={16} />
                {t('addNewField') || 'Add new field'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Right: Field edit panel (inline) — for editing existing OR adding new */}
      {(state.selectedFieldId || state.isAddingCustomField) && (
        <FieldEditPanel canEdit={canEditColumns} addMode={state.isAddingCustomField} />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('confirmDeleteOption') || 'Delete custom field?'}</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deleteTarget && getLocalizedField(deleteTarget, 'columnName')}" and remove it from all views and stages.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel variant="elliptical-outline" disabled={isDeleting}>{t('cancel') || 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction variant="elliptical" onClick={handleDeleteConfirm} disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : (t('delete') || 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add panel shown via FieldEditPanel with addMode */}
    </div>
  )
}
